import type { QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { showWebSocketNotification } from '@shared/utils/websocketNotificationApi';

import { inventoryKeys } from '@features/inventory/api/inventoryApi';
import {
  type InventoryCatalogAssortmentDetails,
  type InventoryCatalogAssortmentListResponse,
  type InventoryCatalogLiveEvent,
  type InventoryPollerStatus,
  type InventoryWarningCode,
  type InventoryListResponse,
  type InventoryFilter,
  type InventoryPrintJob,
  type InventoryPrintJobStatus,
} from '@features/inventory/types';
import { useInventoryCatalogLiveStore } from '@features/inventory/store/useInventoryCatalogLiveStore';
import { useInventoryPrintQueueLiveStore } from '@features/inventory/store/useInventoryPrintQueueLiveStore';
import type { WebSocketEventRouter } from '@/shared/websocket/router';

const LIVE_EVENT_THROTTLE_MS = 500;
const liveEventThrottleTimestamps = new Map<number, number>();

const InventoryEnvelopeSchema = z.object({
  data: z.unknown().optional(),
});

const InventoryEventTypes = new Set([
  'inventory.catalog.live.update',
  'inventory.catalog.equipment.snapshot',
]);

const PrintQueueEventTypes = new Set(['inventory.print.queue.updated']);

const PrintQueueJobSchema = z.object({
  id: z.string(),
  status: z.enum(['queued', 'processing', 'completed', 'failed']),
  mode: z.enum(['barcode', 'barcode_inventory_number', 'barcode_serial_number', 'qr']),
  format: z.enum(['html', 'pdf']).optional().default('pdf'),
  templateId: z.number().nullable().optional(),
  inventoryIds: z.array(z.number()).optional().default([]),
  employeeId: z.number(),
  employeeName: z.string().optional().default(''),
  createdAt: z.string(),
  updatedAt: z.string(),
  error: z.string().nullable().optional(),
  result: z
    .object({
      filename: z.string(),
      contentType: z.string(),
      size: z.number().optional().default(0),
    })
    .nullable()
    .optional(),
});

function toNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value.trim());
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  if (typeof value === 'bigint') {
    return Number(value);
  }
  return undefined;
}

function toStringOrUndefined(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  return undefined;
}

function extractWarnings(raw: unknown): InventoryWarningCode[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const result = new Set<InventoryWarningCode>();
  raw.forEach((item) => {
    if (typeof item !== 'string') {
      return;
    }
    const normalized = item.trim().toLowerCase();
    if (['transit_pending', 'device_offline', 'license_expired', 'reserve_exceeded', 'port_busy', 'decommissioned', 'integration_mismatch', 'api_unavailable', 'poller_unavailable'].includes(normalized)) {
      result.add(normalized as InventoryWarningCode);
    }
  });
  return Array.from(result.values());
}

function resolveSectionFromCache(
  queryClient: QueryClient,
  assortmentId: number,
): number | undefined {
  const cachedLists = queryClient.getQueriesData<InventoryCatalogAssortmentListResponse>({
    queryKey: ['inventory', 'catalog', 'assortments'],
  });

  for (const [, data] of cachedLists) {
    const found = data?.items?.find((item) => item.id === assortmentId);
    if (found) {
      return found.inventorySectionId;
    }
  }

  const details = queryClient.getQueryData<InventoryCatalogAssortmentDetails>(
    inventoryKeys.catalogAssortmentDetails(assortmentId),
  );
  return details?.inventorySectionId;
}

function buildInventoryEvent(
  payload: Record<string, unknown>,
  queryClient: QueryClient,
): InventoryCatalogLiveEvent | null {
  const assortmentId = toNumber(payload.assortmentId ?? payload.assortment_id ?? payload.assortmentID);
  if (!assortmentId) {
    return null;
  }

  const now = Date.now();
  const lastHandled = liveEventThrottleTimestamps.get(assortmentId) ?? 0;
  if (now - lastHandled < LIVE_EVENT_THROTTLE_MS) {
    return null;
  }
  liveEventThrottleTimestamps.set(assortmentId, now);
  if (liveEventThrottleTimestamps.size > 500) {
    const threshold = now - LIVE_EVENT_THROTTLE_MS * 20;
    liveEventThrottleTimestamps.forEach((timestamp, key) => {
      if (timestamp < threshold) {
        liveEventThrottleTimestamps.delete(key);
      }
    });
  }

  const event: InventoryCatalogLiveEvent = {
    assortmentId,
    inventoryId: toNumber(payload.inventoryId ?? payload.inventory_id ?? payload.inventoryID),
    equipmentId: toNumber(payload.equipmentId ?? payload.equipment_id ?? payload.equipmentID),
    sectionId: toNumber(payload.sectionId ?? payload.section_id ?? payload.sectionID),
    source: (toStringOrUndefined(payload.source) as InventoryCatalogLiveEvent['source']) ?? undefined,
    licenseStatus: toStringOrUndefined(payload.licenseStatus ?? payload.license_status),
    warnings: extractWarnings(payload.warnings),
    message: toStringOrUndefined(payload.message),
    pollerMessage: toStringOrUndefined(payload.pollerMessage ?? payload.poller_message),
    generatedAt:
      toStringOrUndefined(payload.generatedAt ?? payload.generated_at ?? payload.timestamp ?? payload.generated) ??
      new Date().toISOString(),
  };

  const pollerRaw = payload.poller ?? payload.pollerStatus ?? payload.poller_status;
  if (typeof pollerRaw === 'object' && pollerRaw !== null) {
    const pollerRecord = pollerRaw as Record<string, unknown>;
    const statusRaw = toStringOrUndefined(pollerRecord.status ?? pollerRecord.Status);
    if (statusRaw && ['online', 'offline', 'unknown'].includes(statusRaw)) {
      event.pollerStatus = statusRaw as InventoryPollerStatus;
    }
    const pollerMessageRaw = pollerRecord.message ?? pollerRecord.Message;
    if (typeof pollerMessageRaw === 'string') {
      event.pollerMessage = pollerMessageRaw;
      if (!event.message) {
        event.message = pollerMessageRaw;
      }
    }
  } else {
    const statusRaw = toStringOrUndefined(pollerRaw);
    if (statusRaw && ['online', 'offline', 'unknown'].includes(statusRaw)) {
      event.pollerStatus = statusRaw as InventoryPollerStatus;
    }
  }

  if (!event.sectionId) {
    event.sectionId = resolveSectionFromCache(queryClient, assortmentId);
  }

  return event;
}

function applyEventToInventoryLists(
  event: InventoryCatalogLiveEvent,
  queryClient: QueryClient,
): void {
  if (!event.inventoryId) {
    return;
  }

  const listQueries = queryClient.getQueriesData<InventoryListResponse>({
    queryKey: ['inventory', 'list'],
  });

  listQueries.forEach(([queryKey, data]) => {
    if (!data) {
      return;
    }

    const typedKey = queryKey as ReturnType<typeof inventoryKeys.list>;
    const filter = typedKey?.[2] as InventoryFilter | undefined;
    const items = data.items ?? [];
    const targetIndex = items.findIndex((item) => item.id === event.inventoryId);
    const warnings = event.warnings ?? [];

    if (targetIndex === -1) {
      if (warnings.length === 0) {
        return;
      }
      if (filter?.warningsOnly) {
        queryClient.invalidateQueries({ queryKey });
      }
      return;
    }

    if (filter?.warningsOnly && warnings.length === 0) {
      const nextItems = items.slice();
      nextItems.splice(targetIndex, 1);
      const nextTotal = Math.max(0, (data.pagination?.total ?? nextItems.length) - 1);
      queryClient.setQueryData(queryKey, {
        ...data,
        items: nextItems,
        pagination: data.pagination
          ? {
              ...data.pagination,
              total: nextTotal,
            }
          : data.pagination,
      });
      return;
    }

    const nextItems = items.slice();
    nextItems[targetIndex] = {
      ...nextItems[targetIndex],
      warnings: warnings.length ? warnings : undefined,
    };

    queryClient.setQueryData(queryKey, {
      ...data,
      items: nextItems,
    });
  });
}

function toInventoryPrintJob(payload: z.infer<typeof PrintQueueJobSchema>): InventoryPrintJob {
  return {
    id: payload.id,
    status: payload.status,
    mode: payload.mode,
    format: payload.format ?? 'pdf',
    templateId: payload.templateId ?? undefined,
    inventoryIds: payload.inventoryIds ?? [],
    employeeId: payload.employeeId,
    employeeName: payload.employeeName ?? '',
    createdAt: payload.createdAt,
    updatedAt: payload.updatedAt,
    error: payload.error ?? undefined,
    result: payload.result
      ? {
          filename: payload.result.filename,
          contentType: payload.result.contentType,
          size: payload.result.size ?? 0,
        }
      : null,
  };
}

function matchesStatusFilter(filter: InventoryPrintJobStatus[] | undefined, status: InventoryPrintJobStatus): boolean {
  if (!filter || filter.length === 0) {
    return true;
  }
  return filter.includes(status);
}

function applyPrintQueueJobToCaches(job: InventoryPrintJob, queryClient: QueryClient) {
  const cachedQueries = queryClient.getQueriesData<{ items: InventoryPrintJob[]; total: number }>({
    queryKey: ['inventory', 'print-jobs'],
  });

  cachedQueries.forEach(([queryKey, data]) => {
    if (!data) {
      return;
    }

    const typedKey = queryKey as ReturnType<typeof inventoryKeys.printJobs>;
    const statusFilter = (typedKey?.[2] as InventoryPrintJobStatus[] | undefined) ?? undefined;
    const limitParam = typedKey?.[3];
    const limit = typeof limitParam === 'number' ? limitParam : undefined;

    const matches = matchesStatusFilter(statusFilter, job.status);
    const existingIndex = data.items.findIndex((item) => item.id === job.id);

    if (!matches && existingIndex === -1) {
      return;
    }

    const nextItems = data.items.slice();
    let totalDelta = 0;

    if (existingIndex !== -1) {
      if (matches) {
        nextItems[existingIndex] = job;
      } else {
        nextItems.splice(existingIndex, 1);
        totalDelta = -1;
      }
    } else if (matches) {
      nextItems.unshift(job);
      totalDelta = 1;
    }

    if (limit && nextItems.length > limit) {
      nextItems.length = limit;
    }

    queryClient.setQueryData(queryKey, {
      ...data,
      items: nextItems,
      total: Math.max(0, data.total + totalDelta),
    });
  });
}

function handlePrintQueueEvent(rawPayload: unknown, queryClient: QueryClient) {
  const envelope = InventoryEnvelopeSchema.safeParse({ data: rawPayload });
  if (!envelope.success) {
    return;
  }

  const payload = envelope.data.data;
  if (typeof payload !== 'object' || payload === null) {
    return;
  }

  const result = PrintQueueJobSchema.safeParse(payload);
  if (!result.success) {
    return;
  }

  const job = toInventoryPrintJob(result.data);
  applyPrintQueueJobToCaches(job, queryClient);
  useInventoryPrintQueueLiveStore.getState().applyJobEvent(job);
}

export interface InventoryEventsDependencies {
  queryClient: QueryClient;
  translate: (key: string, params?: Record<string, unknown>) => string;
}

export function registerInventoryWebSocketHandlers(
  router: WebSocketEventRouter,
  { queryClient, translate }: InventoryEventsDependencies,
): () => void {
  const handleInventoryEvent = (rawPayload: unknown) => {
    const envelope = InventoryEnvelopeSchema.safeParse({ data: rawPayload });
    if (!envelope.success) {
      return;
    }

    const payload = envelope.data.data;
    if (typeof payload !== 'object' || payload === null) {
      return;
    }

    const record = payload as Record<string, unknown>;
    const data =
      typeof record.data === 'object' && record.data !== null
        ? (record.data as Record<string, unknown>)
        : record;

    const event = buildInventoryEvent(data, queryClient);
    if (!event) {
      return;
    }

    useInventoryCatalogLiveStore.getState().applyEvent(event);
    applyEventToInventoryLists(event, queryClient);

    const notificationText = translate('inventory.catalog.notifications.liveUpdate', {
      id: event.assortmentId,
    });
    if (notificationText) {
      showWebSocketNotification('info', notificationText, undefined, 4);
    }

    queryClient.invalidateQueries({ queryKey: inventoryKeys.catalogAssortmentDetails(event.assortmentId) });
  };

  const disposers = [
    ...Array.from(InventoryEventTypes).map((eventType) =>
      router.on(eventType, (event) => handleInventoryEvent(event.data)),
    ),
    ...Array.from(PrintQueueEventTypes).map((eventType) =>
      router.on(eventType, (event) => handlePrintQueueEvent(event.data, queryClient)),
    ),
  ];

  return () => {
    disposers.forEach((dispose) => dispose());
  };
}
